// Shared VR pieces for game panels — the competition rule: everything works
// with bare hands (poke or pinch-ray), so buttons are big and every press
// stops at the panel. A game's VRPanel composes these:
//
//   VRBtn                 a tappable button
//   VRRulesPanel          "How to play" from the registry rules
//   VRLeaderboardPanel    this world / everyone × week / month / all time
//   VRJoinPanel           the floating "Play X / Join / Host / ? / Leaders" panel over the table
//   VRLobbyBody           seated count + Add computer players + Host + Start
//   SeatPanelMount        a panel in your lap at your chair (grab bar to move it)
//   FloatingPanelMount    a panel that follows you around the room (party games, no chair)
//
// All of them render in the table's local frame (floor origin) unless noted.

import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { Billboard, Text } from '@react-three/drei';
import { useXR } from '@react-three/xr';
import { vrColors as colors, noRaycast, Surface } from '../../VR/vrUi';
import { hands, handMode, type HandSide } from '../../VR/hands/handTracking';
import { useVRBuild } from '../../VR/build/vrBuildState';
import { tableAction, type TableEntry } from '../gameStore';
import { useLeaderboard, type LbRange } from '../leaderboardData';
import type { Chair, GameClientDef } from '../types';
import { joinOption, joinTable, seatedCount } from './LobbyKit';

export { colors as vrColors, noRaycast, Surface };

const _c = new THREE.Vector3();

export function VRBtn({ label, x, y, w, h = 0.05, active = false, disabled = false, fontSize = 0.018, onPress, name }: {
  label: string; x: number; y: number; w: number; h?: number; active?: boolean; disabled?: boolean; fontSize?: number; onPress: () => void; name?: string;
}) {
  const [hover, setHover] = useState(false);
  const bg = disabled ? '#1a2633' : active ? colors.teal : hover ? colors.hover : colors.card;
  return (
    <group position={[x, y, 0.006]} name={name ?? `vr-game-${label}`}
      onPointerOver={(e) => { e.stopPropagation(); setHover(true); }} onPointerOut={() => setHover(false)}
      onClick={(e) => { e.stopPropagation(); if (!disabled) onPress(); }}>
      <Surface width={w} height={h} color={bg} r={0.01} />
      <Text raycast={noRaycast} position={[0, 0, 0.003]} fontSize={fontSize} maxWidth={w - 0.012} textAlign="center"
        color={disabled ? colors.muted : active ? '#04211e' : colors.text}>{label}</Text>
    </group>
  );
}


/** VR "How to play": the same rules text as the on-screen guide (no video link in-headset). */
export function VRRulesPanel({ def, y, onClose }: { def: GameClientDef; y: number; onClose: () => void }) {
  const W = 0.8, H = 0.62;
  const text = (def.rules ?? []).map((sec) => `${sec.title.toUpperCase()}\n${sec.lines.map((l) => `• ${l}`).join('\n')}`).join('\n\n');
  return (
    // Swallow presses so cards/buttons behind the guide can't be hit through it.
    <group position={[0, y + H / 2, 0]} name="vr-game-rules"
      onClick={(e) => e.stopPropagation()} onPointerOver={(e) => e.stopPropagation()}>
      <Surface width={W} height={H} color={colors.panel} />
      <Text raycast={noRaycast} position={[-W / 2 + 0.025, H / 2 - 0.03, 0.004]} anchorX="left" fontSize={0.02} color={colors.teal}>{`How to play ${def.label}`}</Text>
      <VRBtn name="vr-game-rules-close" label="Close" x={W / 2 - 0.07} y={H / 2 - 0.035} w={0.11} h={0.04} fontSize={0.014} onPress={onClose} />
      <Text raycast={noRaycast} position={[-W / 2 + 0.025, H / 2 - 0.07, 0.004]} anchorX="left" anchorY="top"
        maxWidth={W - 0.05} fontSize={0.0122} lineHeight={1.35} color={colors.text}>{text}</Text>
    </group>
  );
}

/**
 * Leaderboard in VR: big tabs (This world / Everyone, Week / Month / All
 * time) and the top 8. Centred on its group origin; swallows presses.
 */
export function VRLeaderboardPanel({ def, worldId, onClose, refreshKey }: { def: GameClientDef; worldId: string | null; onClose: () => void; refreshKey?: unknown }) {
  const [scope, setScope] = useState<'world' | 'all'>(worldId ? 'world' : 'all');
  const [range, setRange] = useState<LbRange>('all');
  const { rows, loading, error } = useLeaderboard(def.kind, scope === 'world' ? worldId : null, range, def.ranking, 8, refreshKey ?? 0);
  const W = 0.8, H = 0.66;
  const pts = def.ranking === 'points';
  const top = H / 2;
  return (
    <group name="vr-game-leaderboard" onClick={(e) => e.stopPropagation()} onPointerOver={(e) => e.stopPropagation()}>
      <Surface width={W} height={H} color={colors.panel} />
      <Text raycast={noRaycast} position={[-W / 2 + 0.025, top - 0.035, 0.004]} anchorX="left" fontSize={0.022} color={colors.teal}>{`${def.glyph} ${def.label} leaders`}</Text>
      <VRBtn name="vr-lb-close" label="Close" x={W / 2 - 0.07} y={top - 0.035} w={0.11} h={0.045} fontSize={0.015} onPress={onClose} />
      {worldId && <>
        <VRBtn name="vr-lb-world" label="This world" x={-0.25} y={top - 0.1} w={0.24} h={0.055} active={scope === 'world'} onPress={() => setScope('world')} />
        <VRBtn name="vr-lb-all" label="Everyone" x={0.0} y={top - 0.1} w={0.24} h={0.055} active={scope === 'all'} onPress={() => setScope('all')} />
      </>}
      {(['week', 'month', 'all'] as LbRange[]).map((r, i) => (
        <VRBtn key={r} name={`vr-lb-${r}`} label={r === 'all' ? 'All time' : r === 'week' ? 'Week' : 'Month'} x={-0.26 + i * 0.26} y={top - 0.17} w={0.24} h={0.055}
          active={range === r} onPress={() => setRange(r)} />
      ))}
      <Text raycast={noRaycast} position={[W / 2 - 0.03, top - 0.215, 0.004]} anchorX="right" fontSize={0.013} color={colors.muted}>{pts ? 'points · W–L' : 'wins – losses'}</Text>
      {error || (!loading && !rows.length) ? (
        <Text raycast={noRaycast} position={[0, -0.05, 0.004]} fontSize={0.017} color={colors.muted} maxWidth={W - 0.08} textAlign="center">
          {error || 'No games yet — be the first.'}
        </Text>
      ) : rows.map((r, i) => (
        <group key={r.user_id} position={[0, top - 0.25 - i * 0.048, 0.004]}>
          <Text raycast={noRaycast} position={[-W / 2 + 0.035, 0, 0]} anchorX="left" fontSize={0.019} color={r.is_me ? colors.teal : colors.text}>{`${r.rank}.`}</Text>
          <Text raycast={noRaycast} position={[-W / 2 + 0.09, 0, 0]} anchorX="left" fontSize={0.019} maxWidth={0.42} color={r.is_me ? colors.teal : colors.text}>
            {`${r.name.slice(0, 22)}${r.is_me ? ' (you)' : ''}`}
          </Text>
          <Text raycast={noRaycast} position={[W / 2 - 0.03, 0, 0]} anchorX="right" fontSize={0.019} color={r.is_me ? colors.teal : colors.text}>
            {pts ? `${r.points} · ${r.wins}–${r.losses}` : `${r.wins} – ${r.losses}`}
          </Text>
        </group>
      ))}
    </group>
  );
}

/** Not seated: the panel floating over the table — Play / Join, Host, How to play, Leaders. */
export function VRJoinPanel({ def, pid, entry, worldId, y = 1.25 }: { def: GameClientDef; pid: string; entry: TableEntry; worldId: string; y?: number }) {
  const [rules, setRules] = useState(false);
  const [board, setBoard] = useState(false);
  const st = entry.table!.state;
  const join = joinOption(def, st);
  const canHost = !!def.host && def.host !== 'none' && !st.host && st.phase !== 'over';
  return (
    <Billboard position={[0, y, 0]}>
      <Surface width={0.5} height={0.2} color={colors.panel} />
      <VRBtn name="vr-game-howto" label="How to play" x={def.rules?.length ? -0.12 : 0} y={-0.135} w={0.22} h={0.045} fontSize={0.014}
        active={rules} disabled={!def.rules?.length} onPress={() => { setBoard(false); setRules((v) => !v); }} />
      <VRBtn name="vr-game-leaders" label="Leaders" x={0.12} y={-0.135} w={0.22} h={0.045} fontSize={0.014} active={board} onPress={() => { setRules(false); setBoard((v) => !v); }} />
      {rules && <VRRulesPanel def={def} y={0.13} onClose={() => setRules(false)} />}
      {board && <group position={[0, 0.13 + 0.33, 0]}><VRLeaderboardPanel def={def} worldId={worldId} onClose={() => setBoard(false)} /></group>}
      <Text raycast={noRaycast} position={[0, 0.055, 0.004]} fontSize={0.024} color={colors.text}>{def.label}</Text>
      <Text raycast={noRaycast} position={[0, 0.02, 0.004]} fontSize={0.015} color={colors.muted}>
        {st.phase === 'lobby' ? `${seatedCount(st)}/${def.maxPlayers} seated` : join ? (st.phase === 'playing' && def.joinMidGame ? 'Game on — jump in' : 'Take over a computer seat') : 'Game in progress — watching'}
      </Text>
      <VRBtn name="vr-game-play" label={st.phase === 'lobby' ? `Play ${def.label}` : 'Join'} x={canHost ? -0.085 : 0} y={-0.045} w={canHost ? 0.22 : 0.3} h={0.06}
        disabled={!join || entry.busy} onPress={() => void joinTable(pid, st)} />
      {canHost && <VRBtn name="vr-game-host" label="Host" x={0.155} y={-0.045} w={0.14} h={0.06} disabled={entry.busy} onPress={() => void tableAction(pid, 'host')} />}
    </Billboard>
  );
}

/** Lobby body for a seated player / host: count, Add computer players, Host, Start. Centred at y≈0.06. */
export function VRLobbyBody({ def, pid, entry }: { def: GameClientDef; pid: string; entry: TableEntry }) {
  const st = entry.table!.state;
  const n = seatedCount(st);
  const full = st.seats.every(Boolean);
  const needHost = def.host === 'required' && !st.host;
  const ready = n >= def.minPlayers && !needHost;
  const left = def.bots ? 'Add computer players' : def.host && def.host !== 'none' && !st.host ? 'Host' : null;
  return <>
    <Text raycast={noRaycast} position={[0, 0.1, 0.004]} fontSize={0.02} color={colors.text}>
      {`${n}/${def.maxPlayers} seated${st.host ? ` · host ${st.host.name}` : needHost ? ' · needs a host' : ''}`}
    </Text>
    {left && <VRBtn label={left} x={-0.19} y={0.02} w={0.34} disabled={(def.bots ? full : false) || entry.busy}
      onPress={() => void tableAction(pid, def.bots ? 'fill_bots' : 'host')} />}
    <VRBtn label="Start" x={left ? 0.19 : 0} y={0.02} w={0.34} active={ready} disabled={!ready || entry.busy} onPress={() => void tableAction(pid, 'start')} />
  </>;
}

const _hw = new THREE.Vector3();
const _fw = new THREE.Vector3();
const _tw = new THREE.Vector3();

/**
 * A panel that stays with you anywhere in the room (party games: buzzers,
 * guess pads). Sits ~0.5 m ahead at chest height and re-centres when you walk
 * off or turn away (> 45°), so it doesn't swim with every head bob.
 */
export function FloatingPanelMount({ children, distance = 0.5, drop = 0.3 }: { children: React.ReactNode; distance?: number; drop?: number }) {
  const ref = useRef<THREE.Group>(null);
  const { camera } = useThree();
  const anchor = useRef<THREE.Vector3 | null>(null);
  useFrame(() => {
    const g = ref.current;
    if (!g?.parent) return;
    camera.getWorldPosition(_hw);
    camera.getWorldDirection(_fw); _fw.y = 0;
    if (_fw.lengthSq() < 1e-6) _fw.set(0, 0, -1);
    _fw.normalize();
    _tw.copy(_hw).addScaledVector(_fw, distance); _tw.y = _hw.y - drop;
    const a = anchor.current;
    const off = a ? Math.hypot(a.x - _hw.x, a.z - _hw.z) : Infinity;
    const toA = a ? _c.set(a.x - _hw.x, 0, a.z - _hw.z).normalize().dot(_fw) : -1;
    if (!a || off > distance + 0.6 || off < 0.15 || toA < Math.cos(Math.PI / 4) || Math.abs(a.y - _tw.y) > 0.35) {
      anchor.current = _tw.clone();
    }
    g.position.copy(g.parent.worldToLocal(_c.copy(anchor.current!)));
    g.lookAt(_hw);
  });
  return <group ref={ref}>{children}</group>;
}

// ---- Seated panel placement -------------------------------------------------
// Default: in your lap — about 40 cm below the eyes, just in front of the chair,
// tilted up at your face like a hand of cards — so the table and the other
// players stay in view over it. The player can grab the bar on its top edge and
// move it; that offset (in the seat's own frame, so it suits every chair) is
// remembered across games.

const PANEL_FORWARD = 0.42;  // panel centre this far in front of the chair (Spades: 0.6 m from the table centre)
const PANEL_DROP = 0.38;      // below eye height
const PANEL_OFFSET_KEY = 'gx-spades-vr-panel-v1'; // (one offset for every game's seated panel)
type PanelOffset = { x: number; y: number; z: number };
const ZERO_OFFSET: PanelOffset = { x: 0, y: 0, z: 0 };

function loadPanelOffset(): PanelOffset {
  try {
    const v = JSON.parse(localStorage.getItem(PANEL_OFFSET_KEY) || 'null');
    if (v && [v.x, v.y, v.z].every((n) => typeof n === 'number' && Number.isFinite(n))) return clampOffset(v);
  } catch { /* storage blocked */ }
  return { ...ZERO_OFFSET };
}
function savePanelOffset(o: PanelOffset) {
  try {
    if (o.x === 0 && o.y === 0 && o.z === 0) localStorage.removeItem(PANEL_OFFSET_KEY);
    else localStorage.setItem(PANEL_OFFSET_KEY, JSON.stringify(o));
  } catch { /* storage blocked */ }
}
function clampOffset(o: PanelOffset): PanelOffset {
  const c = THREE.MathUtils.clamp;
  // Local frame: +Z points back toward the chair, +X to the player's left.
  return { x: c(o.x, -0.7, 0.7), y: c(o.y, -0.35, 0.6), z: c(o.z, -0.45, 0.3) };
}
/** Shared so the offset survives the panel remounting between phases. */
const panelOffset: { current: PanelOffset } = { current: loadPanelOffset() };

const _hl = new THREE.Vector3();
const _a = new THREE.Vector3();
const _b = new THREE.Vector3();

/** The grab bar on the panel's top edge: pinch it (or point a controller at it
 *  and press) and move your hand — the panel follows until you let go.
 *  Tracked hands are followed directly from the pinch (pointer capture can
 *  drop a held hand pinch); controllers use the captured pointer. */
function PanelGrabBar({ y, baseRef, onMoved }: {
  y: number;
  baseRef: React.RefObject<THREE.Group | null>;
  onMoved: (o: PanelOffset, done: boolean) => void;
}) {
  const [hover, setHover] = useState(false);
  const [dragging, setDragging] = useState(false);
  const origin = useXR((s) => s.origin);
  const drag = useRef<null | { kind: 'hand'; side: HandSide; dist: number; start: THREE.Vector3; from: PanelOffset }
    | { kind: 'pointer'; id: number; start: THREE.Vector3; from: PanelOffset }>(null);
  const W = 0.24, H = 0.034;
  const lit = hover || dragging;
  useEffect(() => () => { if (drag.current) useVRBuild.getState().setHandling(false); }, []);

  const apply = (local: THREE.Vector3, done: boolean) => {
    const d = drag.current!;
    onMoved(clampOffset({ x: d.from.x + (local.x - d.start.x), y: d.from.y + (local.y - d.start.y), z: d.from.z + (local.z - d.start.z) }), done);
  };
  const finish = () => {
    drag.current = null;
    setDragging(false);
    useVRBuild.getState().setHandling(false);
    onMoved(panelOffset.current, true);
  };

  useFrame(() => {
    const d = drag.current, base = baseRef.current;
    if (!d || d.kind !== 'hand' || !base || !origin) return;
    const h = hands[d.side];
    if (!h.pinching || !h.present) { finish(); return; }
    origin.updateMatrixWorld();
    _a.copy(h.rayOrigin).applyMatrix4(origin.matrixWorld);
    _b.copy(h.rayDir).transformDirection(origin.matrixWorld);
    _a.addScaledVector(_b, d.dist);
    apply(base.worldToLocal(_a), false);
  });

  return (
    <group position={[0, y, 0.008]} name="vr-game-panel-grab"
      onPointerOver={(e) => { e.stopPropagation(); setHover(true); }}
      onPointerOut={() => setHover(false)}
      onPointerDown={(e) => {
        e.stopPropagation();
        const base = baseRef.current;
        if (!base || drag.current) return;
        const start = base.worldToLocal(_c.copy(e.point)).clone();
        const from = { ...panelOffset.current };
        // Which tracked hand is pinching along this ray?
        let side: HandSide | null = null, best = Infinity, dist = 0;
        if (handMode.active && origin) {
          origin.updateMatrixWorld();
          for (const sd of ['right', 'left'] as HandSide[]) {
            const h = hands[sd];
            if (!h.present) continue;
            _a.copy(h.rayOrigin).applyMatrix4(origin.matrixWorld);
            _b.copy(h.rayDir).transformDirection(origin.matrixWorld);
            const t = _c.copy(e.point).sub(_a).dot(_b);
            const miss = _c.copy(_a).addScaledVector(_b, t).distanceTo(e.point);
            if (t > 0 && miss < best) { best = miss; side = sd; dist = t; }
          }
        }
        if (side && best < 0.15) {
          drag.current = { kind: 'hand', side, dist, start, from };
        } else {
          try { (e.target as any).setPointerCapture?.(e.pointerId); } catch { /* not capturable */ }
          drag.current = { kind: 'pointer', id: e.pointerId, start, from };
        }
        // "Holding something" owns the pinch: the hand controls won't turn it
        // into a teleport when it's released.
        useVRBuild.getState().setHandling(true);
        setDragging(true);
      }}
      onPointerMove={(e) => {
        const d = drag.current, base = baseRef.current;
        if (!d || d.kind !== 'pointer' || !base || e.pointerId !== d.id) return;
        e.stopPropagation();
        apply(base.worldToLocal(_b.copy(e.point)), false);
      }}
      onPointerUp={(e) => {
        const d = drag.current;
        if (!d || d.kind !== 'pointer' || e.pointerId !== d.id) return;
        e.stopPropagation();
        try { (e.target as any).releasePointerCapture?.(e.pointerId); } catch { /* already released */ }
        finish();
      }}
      // Swallow the click so a grab never reaches a card or teleports.
      onClick={(e) => e.stopPropagation()}>
      <Surface width={W} height={H} color={lit ? colors.teal : colors.card} r={0.017} />
      {/* Grip dots */}
      {[-1, 0, 1].map((i) => [-1, 1].map((j) => (
        <mesh key={`${i}${j}`} raycast={noRaycast} position={[i * 0.014, j * 0.006, 0.003]}>
          <circleGeometry args={[0.0032, 10]} />
          <meshBasicMaterial color={lit ? '#04211e' : colors.muted} toneMapped={false} />
        </mesh>
      )))}
      {hover && !dragging && (
        <Text raycast={noRaycast} position={[0, H / 2 + 0.016, 0.003]} fontSize={0.012} color={colors.muted}>Pinch and drag to move</Text>
      )}
    </group>
  );
}

/**
 * Holds the seated player's panel in front of their chair. It follows the real
 * head height (so it reads the same seated or standing at home), turns to face
 * the head, and carries the player's own move offset.
 */
export function SeatPanelMount({ chair, height, children }: { chair: Chair; height: number; children: React.ReactNode }) {
  const base = useRef<THREE.Group>(null);
  const mover = useRef<THREE.Group>(null);
  const facer = useRef<THREE.Group>(null);
  const settled = useRef(false);
  const [moved, setMoved] = useState(() => {
    const o = panelOffset.current; return o.x !== 0 || o.y !== 0 || o.z !== 0;
  });
  const { camera } = useThree();
  // In front of the chair, toward the table (forward = (sin yaw, cos yaw)).
  const x = chair.x + Math.sin(chair.yaw) * PANEL_FORWARD, z = chair.z + Math.cos(chair.yaw) * PANEL_FORWARD;
  useFrame(() => {
    const b = base.current, m = mover.current, f = facer.current;
    if (!b || !m || !f) return;
    camera.getWorldPosition(_hl);
    b.worldToLocal(_hl); // head in the panel's seat frame
    const o = panelOffset.current;
    const targetY = _hl.y - PANEL_DROP + o.y;
    // Snap on first frame / big jumps (sitting down), ease small head bobs.
    const k = !settled.current || Math.abs(targetY - m.position.y) > 0.5 ? 1 : 0.1;
    settled.current = true;
    m.position.set(o.x, m.position.y + (targetY - m.position.y) * k, o.z);
    // Face the head: yaw toward it, then tilt up at it.
    const dx = _hl.x - m.position.x, dz = _hl.z - m.position.z;
    const flat = Math.max(0.15, Math.hypot(dx, dz));
    f.rotation.set(-Math.atan2(_hl.y - m.position.y, flat), Math.atan2(dx, dz), 0, 'YXZ');
  });
  const onMoved = (o: PanelOffset, done: boolean) => {
    panelOffset.current = o;
    if (done) { savePanelOffset(o); setMoved(o.x !== 0 || o.y !== 0 || o.z !== 0); }
  };
  return (
    <group ref={base} position={[x, 0, z]} rotation={[0, chair.yaw + Math.PI, 0]}>
      <group ref={mover} position={[0, 1.0, 0]}>
        <group ref={facer}>
          {children}
          <PanelGrabBar y={height / 2 + 0.03} baseRef={base} onMoved={onMoved} />
          {moved && (
            <VRBtn name="vr-game-panel-reset" label="Reset" x={0.2} y={height / 2 + 0.03} w={0.09} h={0.032} fontSize={0.012}
              onPress={() => { onMoved({ ...ZERO_OFFSET }, true); }} />
          )}
        </group>
      </group>
    </group>
  );
}
